import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';
import type { TrafficLight } from '@shared/api/hooks/useReceipts';

// Re-export for convenience when consumers need only analyze types.
export type { TrafficLight };

export type MatrixDecision = 'AUTO_APPROVE' | 'REVIEW' | 'REJECT' | 'REJECT_FLAG';
export type SignalKind = 'hard' | 'soft';

export interface AnalyzeSignal {
  code: string;
  weight: number;
  detail: string;
  kind: SignalKind;
}

export interface AnalyzeReceiptResult {
  imageInfo: {
    format: string;
    width: number;
    height: number;
    sizeBytes: number;
  };
  reading: {
    confidence: number;
    light: TrafficLight;
    fields: {
      storeName: string | null;
      partitaIva: string | null;
      businessName: string | null;
      date: string | null;
      totalAmount: number | null;
      receiptNumber: string | null;
    };
  };
  authenticity: {
    score: number;
    light: TrafficLight;
    hardFail: boolean;
    hasHard: boolean;
    signals: AnalyzeSignal[];
  };
  disparity: number;
  matrixDecision: MatrixDecision;
  isDisparityFlag: boolean;
  sponsor: {
    matched: boolean;
    name: string | null;
    matchedBy: string | null;
  };
  duplicate: {
    found: boolean;
    receiptId: string | null;
  };
  metadata: {
    source: string;
    screenLike: boolean;
    exif: Record<string, unknown>;
    c2pa: Record<string, unknown>;
  };
}

/**
 * Invia un file immagine (multipart/form-data, campo `file`) a POST /receipts/admin/analyze
 * e restituisce il risultato dell'analisi antifrode.
 *
 * Usage:
 *   const mutation = useAnalyzeReceipt();
 *   mutation.mutate(file, { onSuccess: (result) => ... });
 */
export function useAnalyzeReceipt() {
  return useMutation<AnalyzeReceiptResult, Error, File>({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post<AnalyzeReceiptResult>('/receipts/admin/analyze', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
  });
}
